import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, MapPin, Clock, CalendarDays, Pencil, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blankForm = {
  location: "",
  address: "",
  event_date: "",
  start_time: "11:00 AM",
  end_time: "8:00 PM",
  notes: "",
  active: true,
  recurrence: "none",
  recurrence_end: "",
};

// Convert ISO/UTC string → value acceptable to <input type="datetime-local">
const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const FoodTruckScheduleManager = () => {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // viewMode:  "next" = one row per active stop (recurring shows next occurrence)
  //            "all"  = every upcoming occurrence within 12 weeks
  //            "past" = raw parents including past + inactive
  const [viewMode, setViewMode] = useState("next");

  const fetchStops = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/food-truck/schedule?upcoming_only=true&expand=next`;
      if (viewMode === "all") {
        url = `${BACKEND_URL}/api/food-truck/schedule?upcoming_only=true&expand=all`;
      } else if (viewMode === "past") {
        url = `${BACKEND_URL}/api/food-truck/schedule?upcoming_only=false`;
      }
      const { data } = await axios.get(url, { withCredentials: true });
      setStops(data);
    } catch (e) {
      toast.error("Failed to load food truck schedule");
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchStops();
  }, [fetchStops]);

  const resetForm = () => {
    setEditingId(null);
    setForm(blankForm);
  };

  const startEdit = (stop) => {
    setEditingId(stop.id);
    setForm({
      location: stop.location || "",
      address: stop.address || "",
      event_date: toDatetimeLocal(stop.event_date),
      start_time: stop.start_time || "11:00 AM",
      end_time: stop.end_time || "8:00 PM",
      notes: stop.notes || "",
      active: stop.active !== false,
      recurrence: stop.recurrence || "none",
      recurrence_end: toDateInput(stop.recurrence_end),
    });
    // Scroll form into view on mobile
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location || !form.event_date) {
      toast.error("Location and date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        event_date: new Date(form.event_date).toISOString(),
      };
      if (form.recurrence_end) {
        payload.recurrence_end = new Date(form.recurrence_end).toISOString();
      } else {
        payload.recurrence_end = null;
      }
      if (editingId) {
        await axios.put(
          `${BACKEND_URL}/api/admin/food-truck/schedule/${editingId}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Stop updated");
      } else {
        // omit null recurrence_end on create for backward-compat
        if (payload.recurrence_end === null) delete payload.recurrence_end;
        await axios.post(
          `${BACKEND_URL}/api/admin/food-truck/schedule`,
          payload,
          { withCredentials: true }
        );
        toast.success("Stop added");
      }
      resetForm();
      fetchStops();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save stop");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (stopId) => {
    if (!window.confirm("Delete this stop?")) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/food-truck/schedule/${stopId}`,
        { withCredentials: true }
      );
      toast.success("Stop deleted");
      if (editingId === stopId) resetForm();
      fetchStops();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (stop) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/food-truck/schedule/${stop.id}`,
        { active: !stop.active },
        { withCredentials: true }
      );
      fetchStops();
    } catch {
      toast.error("Failed to update");
    }
  };

  const isEditing = !!editingId;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="food-truck-schedule-manager">
      {/* Form */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Pencil className="h-5 w-5 text-emerald-600" />
                Edit Stop
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 text-emerald-600" />
                Add a Stop
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label>Location *</Label>
              <Input
                data-testid="ft-form-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Sundance Square"
                required
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                data-testid="ft-form-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Main St, Fort Worth"
              />
            </div>
            <div>
              <Label>Date & Time *</Label>
              <Input
                data-testid="ft-form-date"
                type="datetime-local"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  placeholder="11:00 AM"
                />
              </div>
              <div>
                <Label>End</Label>
                <Input
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  placeholder="8:00 PM"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional — event details"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Repeats</Label>
                <select
                  data-testid="ft-form-recurrence"
                  value={form.recurrence}
                  onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                >
                  <option value="none">Doesn't repeat</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                </select>
              </div>
              <div>
                <Label>Ends (optional)</Label>
                <Input
                  type="date"
                  data-testid="ft-form-recurrence-end"
                  value={form.recurrence_end}
                  onChange={(e) => setForm({ ...form, recurrence_end: e.target.value })}
                  disabled={form.recurrence === "none"}
                />
              </div>
            </div>
            {form.recurrence !== "none" && (
              <p className="text-xs text-gray-500 -mt-1">
                Repeats {form.recurrence === "weekly" ? "every week" : "every 2 weeks"}{" "}
                {form.recurrence_end ? `until ${form.recurrence_end}` : "for the next 12 weeks"}.
              </p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="ft-form-active"
                />
                Show this stop publicly
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={saving}
                data-testid="ft-form-submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {saving
                  ? isEditing
                    ? "Saving..."
                    : "Adding..."
                  : isEditing
                  ? "Save Changes"
                  : "Add Stop"}
              </Button>
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  data-testid="ft-form-cancel"
                >
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle>Scheduled Stops</CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-600">Show:</Label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                data-testid="ft-view-mode"
                className="border rounded px-3 py-1.5 text-sm bg-white"
              >
                <option value="next">Upcoming (next per series)</option>
                <option value="all">All upcoming occurrences</option>
                <option value="past">Include past &amp; inactive</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : stops.length === 0 ? (
            <p className="text-gray-500 text-center py-8" data-testid="ft-no-stops">
              {viewMode === "past"
                ? "No stops scheduled yet. Add one to the left."
                : "No upcoming stops. Add one to the left."}
            </p>
          ) : (
            <div className="space-y-3">
              {stops.map((stop) => {
                const dt = new Date(stop.event_date);
                const isPast = dt < new Date();
                const isRecurring = stop.recurrence && stop.recurrence !== "none";
                const isRowEditing = editingId === stop.id;
                return (
                  <Card
                    key={`${stop.id}-${stop.event_date}`}
                    data-testid={`ft-stop-row-${stop.id}`}
                    className={`${isPast ? "opacity-60" : ""} ${
                      isRowEditing ? "ring-2 ring-emerald-500" : ""
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <MapPin className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                            <h4 className="font-semibold text-gray-900 truncate">
                              {stop.location}
                            </h4>
                            {!stop.active && (
                              <Badge variant="outline" className="text-xs">Inactive</Badge>
                            )}
                            {isRecurring && (
                              <Badge className="text-xs bg-emerald-100 text-emerald-800 border-emerald-200">
                                {stop.recurrence === "weekly" ? "Weekly" : "Every 2 weeks"}
                              </Badge>
                            )}
                            {isPast && (
                              <Badge variant="outline" className="text-xs">Past</Badge>
                            )}
                            {isRowEditing && (
                              <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200">
                                Editing
                              </Badge>
                            )}
                          </div>
                          {stop.address && (
                            <p className="text-sm text-gray-500 ml-6">{stop.address}</p>
                          )}
                          <div className="flex items-center gap-1 text-sm text-gray-700 ml-6 mt-1">
                            <CalendarDays className="h-3.5 w-3.5 text-gray-400" />
                            {dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                            {isRecurring && viewMode === "next" && (
                              <span className="text-xs text-emerald-700 ml-1">(next)</span>
                            )}
                            <Clock className="h-3.5 w-3.5 text-gray-400 ml-2" />
                            {stop.start_time} – {stop.end_time}
                          </div>
                          {stop.notes && (
                            <p className="text-xs text-gray-500 italic ml-6 mt-1">{stop.notes}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(stop)}
                            data-testid={`ft-edit-${stop.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleActive(stop)}
                            data-testid={`ft-toggle-${stop.id}`}
                          >
                            {stop.active ? "Hide" : "Show"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(stop.id)}
                            data-testid={`ft-delete-${stop.id}`}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
